import { useState } from 'react';
import { createBadge, updateBadge } from '../../services/content/badgeService';
import { ImageUploadField } from './ImageUploadField';
import { useAssociationLookup } from '../../hooks/useAssociationLookup';
import type { Badge, LMSAssociation } from '../../types/content/badge';

const associationOptions: { value: LMSAssociation; label: string }[] = [
  { value: 'certification', label: 'Certification' },
  { value: 'course group', label: 'Course Group' },
  { value: 'course', label: 'Course' },
  { value: 'category', label: 'Category' },
  { value: 'lesson', label: 'Lesson' },
];

interface BadgeModalProps {
  badge?: Badge;
  onClose: () => void;
  onSuccess: () => void;
}

export function BadgeModal({ badge, onClose, onSuccess }: BadgeModalProps) {
  const isEdit = !!badge;
  const { getOptionsForAssociation } = useAssociationLookup();

  const [name, setName] = useState(badge?.name || '');
  const [description, setDescription] = useState(badge?.description || '');
  const [template, setTemplate] = useState(badge?.template || '');
  const [category, setCategory] = useState(badge?.category || '');
  const [association, setAssociation] = useState<string>(badge?.association || '');
  const [associationId, setAssociationId] = useState(badge?.associationId || '');
  const [imageUrl, setImageUrl] = useState(badge?.imageUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  async function handleSave() {
    if (!name.trim() || !category.trim()) return;
    setError('');
    setLoading(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || null,
        template: template.trim() || null,
        category: category.trim(),
        association: (association as LMSAssociation) || null,
        associationId: associationId.trim() || null,
      };
      if (isEdit) {
        await updateBadge(badge!.id, { ...badge!, ...data });
      } else {
        await createBadge(data);
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update badge' : 'Failed to create badge');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {isEdit ? 'Edit Badge' : 'Add Badge'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Badge name" />
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} rows={3} placeholder="Badge description" />
            </div>
            <div>
              <label className={labelClass}>Category *</label>
              <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass} placeholder="e.g. achievement" />
            </div>
            <div>
              <label className={labelClass}>Template</label>
              <textarea value={template} onChange={(e) => setTemplate(e.target.value)} className={inputClass} rows={3} placeholder="Badge template" />
            </div>
            <div>
              <label className={labelClass}>Association</label>
              <select value={association} onChange={(e) => setAssociation(e.target.value)} className={inputClass}>
                <option value="">None</option>
                {associationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {association && (() => {
              const options = getOptionsForAssociation(association);
              return options.length > 0 ? (
                <div>
                  <label className={labelClass}>Associated {association}</label>
                  <select value={associationId} onChange={(e) => setAssociationId(e.target.value)} className={inputClass}>
                    <option value="">Select...</option>
                    {options.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className={labelClass}>Association ID</label>
                  <input type="text" value={associationId} onChange={(e) => setAssociationId(e.target.value)} className={inputClass} placeholder="UUID of associated entity" />
                </div>
              );
            })()}
            {isEdit && badge?.id && (
              <ImageUploadField
                label="Badge Image"
                currentUrl={imageUrl}
                uploadEndpoint={`/badge/${badge.id}/image`}
                onUploaded={() => onSuccess()}
              />
            )}
          </div>

          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={loading || !name.trim() || !category.trim()} className={btnPrimary}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
